import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LuUsers, LuSearch, LuFilter } from "react-icons/lu";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";
import type { ConsoleUserRow } from "@/lib/api";

export function UsersPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [tier, setTier] = useState<string>("");

  const usersQuery = useQuery({
    queryKey: ["console", "users", { q, tier }],
    queryFn: () => api.users.list({ q: q || undefined, tier: tier || undefined }),
  });

  const columns: Column<ConsoleUserRow>[] = [
    { header: "Email", cell: (u) => <span className="text-[var(--color-text)]">{u.email}</span> },
    { header: "Tier", cell: (u) => <TierBadge tier={u.subscriptionTier} /> },
    { header: "Status", cell: (u) => <StatusBadge status={u.subscriptionStatus} /> },
    {
      header: "Role",
      cell: (u) => (
        <span className="text-xs uppercase tracking-wider text-[var(--color-text-subtle)]">
          {u.role}
        </span>
      ),
    },
    {
      header: "Last seen",
      cell: (u) => <LastSeenCell timestamp={u.lastSeenAt ?? null} />,
    },
    {
      header: "Last login",
      cell: (u) => (
        <span className="text-xs text-[var(--color-text-muted)] tabular-nums">
          {u.lastLoginAt ? formatRelative(u.lastLoginAt) : "never"}
        </span>
      ),
    },
    {
      header: "Joined",
      cell: (u) => new Date(u.createdAt).toLocaleDateString(),
      className: "text-[var(--color-text-muted)]",
    },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        icon={LuUsers}
        description={
          usersQuery.data
            ? `${usersQuery.data.total.toLocaleString()} accounts in the product database`
            : "Loading..."
        }
      />
      <div className="p-8 space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-subtle)] pointer-events-none" />
            <input
              type="search"
              placeholder="Search by email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)]"
            />
          </div>
          <div className="relative">
            <LuFilter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-subtle)] pointer-events-none" />
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              className="pl-10 pr-8 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)] appearance-none"
            >
              <option value="">All tiers</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="business">Business</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>
        <Card>
          <DataTable
            columns={columns}
            rows={usersQuery.data?.users}
            isLoading={usersQuery.isLoading}
            isError={usersQuery.isError}
            rowKey={(u) => u.id}
            onRowClick={(u) => navigate(`/users/${u.id}`)}
            emptyMessage="No users match the filters."
          />
        </Card>
      </div>
    </>
  );
}

const TIER_COLOURS: Record<string, string> = {
  free: "bg-[var(--color-elevated)] text-[var(--color-text-muted)] border-[var(--color-border)]",
  pro: "bg-[rgba(139,92,246,0.12)] text-[var(--color-accent-light)] border-[rgba(139,92,246,0.3)]",
  business: "bg-[rgba(65,209,255,0.1)] text-[#41d1ff] border-[rgba(65,209,255,0.3)]",
  enterprise: "bg-[rgba(189,52,254,0.12)] text-[#d8b4fe] border-[rgba(189,52,254,0.3)]",
};

function TierBadge({ tier }: { tier: string }) {
  const cls = TIER_COLOURS[tier] ?? TIER_COLOURS.free;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[11px] uppercase tracking-wider rounded-md border ${cls}`}
    >
      {tier}
    </span>
  );
}

const STATUS_COLOURS: Record<string, string> = {
  active: "text-[#6ee7b7]",
  cancelled: "text-[var(--color-text-subtle)]",
  past_due: "text-[#fdba74]",
  trialing: "text-[#fcd34d]",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLOURS[status] ?? "text-[var(--color-text-muted)]";
  return (
    <span className={`text-xs flex items-center gap-1.5 ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {status}
    </span>
  );
}

/**
 * Render a "last seen" cell with a coloured dot:
 *   - online   - seen in the last 5 minutes
 *   - active   - seen in the last 24 hours
 *   - recent   - seen in the last 30 days
 *   - dormant  - older than 30 days, or never logged in
 */
function LastSeenCell({ timestamp }: { timestamp: string | null }) {
  if (!timestamp) {
    return (
      <span className="text-xs flex items-center gap-1.5 text-[var(--color-text-subtle)]">
        <span className="w-1.5 h-1.5 rounded-full bg-current" />
        never
      </span>
    );
  }
  const ageMs = Date.now() - new Date(timestamp).getTime();
  const fiveMin = 5 * 60_000;
  const oneDay = 24 * 60 * 60_000;
  const thirtyDays = 30 * oneDay;
  let cls = "text-[var(--color-text-subtle)]";
  let label = formatRelative(timestamp);
  if (ageMs < fiveMin) {
    cls = "text-[#6ee7b7]";
    label = "online";
  } else if (ageMs < oneDay) {
    cls = "text-[#41d1ff]";
  } else if (ageMs < thirtyDays) {
    cls = "text-[var(--color-text-muted)]";
  }
  return (
    <span
      className={`text-xs flex items-center gap-1.5 tabular-nums ${cls}`}
      title={new Date(timestamp).toLocaleString()}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

/**
 * Compact relative-time formatter: "5m ago", "2h ago", "3d ago", or
 * absolute date for anything older than 30 days.
 */
function formatRelative(timestamp: string): string {
  const ageMs = Date.now() - new Date(timestamp).getTime();
  if (ageMs < 60_000) return `${Math.max(1, Math.floor(ageMs / 1000))}s ago`;
  if (ageMs < 3_600_000) return `${Math.floor(ageMs / 60_000)}m ago`;
  if (ageMs < 86_400_000) return `${Math.floor(ageMs / 3_600_000)}h ago`;
  if (ageMs < 30 * 86_400_000) return `${Math.floor(ageMs / 86_400_000)}d ago`;
  return new Date(timestamp).toLocaleDateString();
}
